import {useContext, useEffect, useState} from "react";
import { PieChart, Pie, Tooltip, Cell, Legend, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ProjectInfo } from "../../../models/ProjectForm";
import projectFormService from "../../../services/forms/projectForm.service";
import './Statistics.css';
import { Typography } from "@material-tailwind/react";
import AppTitleContext from "../../../context/AppTitleContext";

const Statistics = () => {
    const [projects, setProjects] = useState<ProjectInfo[]>([]);
    const [programCounts, setProgramCounts] = useState([] as { name: string, value: number }[]);
    const [activeSection, setActiveSection] = useState('pie');
    const [categoryCounts, setCategoryCounts] = useState([] as { name: string, value: number }[]);
    const [genderCounts, setGenderCounts] = useState([] as { name: string, value: number }[]);
    const { updateAppTitle } = useContext(AppTitleContext);

    // Colores para las gráficas
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#A4DE6C', '#D0ED57', '#FFB6C1'];
    const COLORSBAR1= ['#feb310','#003fa4','#50b848']
    const COLORSBAR2 = ['blue', 'purple', 'pink']


    async function getProjects() {
        try {
            let response = await projectFormService.getAllProjects();
            setProjects(response.projects);
        } catch (error) {
            console.error("Error fetching projects:", error);
        }
    }

    function countProgramEducation() {
        const counts = projects.reduce((acc, project) => {
            project.members.forEach(member => {
                acc[member.eduProgram] = (acc[member.eduProgram] || 0) + 1;
            });
            return acc;
        }, {} as { [key: string]: number });

        const data = Object.keys(counts).map(program => ({
            name: program,
            value: counts[program]
        }));
        setProgramCounts(data);
    }

    function countCategoryProjects() {
        const counts = projects.reduce((acc, project) => {
            acc[project.category] = (acc[project.category] || 0) + 1;
            return acc;
        }, {} as { [key: string]: number });

        const data = Object.keys(counts).map(category => ({
            name: category,
            value: counts[category]
        }));
        setCategoryCounts(data);
    }
    function countGenderMembers() {
        const counts = projects.reduce((acc, project) => {
            project.members.forEach(member => {
                acc[member.gender] = (acc[member.gender] || 0) + 1;
            });
            return acc;
        }, {} as { [key: string]: number });

        const data = Object.keys(counts).map(gender => ({
            name:gender,
            value:counts[gender]
        }));
        setGenderCounts(data);
    }

    useEffect(() => {
        getProjects();
        updateAppTitle([{title: 'Estadisticas'}]);
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            countProgramEducation()
            countCategoryProjects();
            countGenderMembers()
        }
    }, [projects]);

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <button
                    onClick={() => setActiveSection('pie')}
                    style={{
                        borderBottom: activeSection === 'pie' ? '2px solid #dd971a' : 'none',
                        padding: '10px',
                        cursor: 'pointer'
                    }}
                >
                    Programas Educativos
                </button>
                <button
                    onClick={() => setActiveSection('bar1')}
                    style={{
                        borderBottom: activeSection === 'bar1' ? '2px solid #dd971a' : 'none',
                        padding: '10px',
                        cursor: 'pointer'
                    }}
                >
                    Categoría de Proyectos
                </button>
                <button
                    onClick={() => setActiveSection('bar2')}
                    style={{
                        borderBottom: activeSection === 'bar2' ? '2px solid #dd971a' : 'none',
                        padding: '10px',
                        cursor: 'pointer'
                    }}
                >
                    Integrantes por Género
                </button>
                {/*<button*/}
                {/*    onClick={() => setActiveSection('line')}*/}
                {/*    style={{*/}
                {/*        borderBottom: activeSection === 'line' ? '2px solid #dd971a' : 'none',*/}
                {/*        padding: '10px',*/}
                {/*        cursor: 'pointer'*/}
                {/*    }}*/}
                {/*>*/}
                {/*    Gráfica de Líneas*/}
                {/*</button>*/}
            </div>

            {/* Renderización de la sección activa */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
                {activeSection === 'pie' && (
                    <ResponsiveContainer width="80%" height="100%">
                        <PieChart>
                            <Pie
                                data={programCounts}
                                cx="50%"
                                cy="50%"
                                outerRadius={150}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {programCounts.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                )}
                {activeSection === 'bar1' && (
                    <ResponsiveContainer width="80%" height="100%">
                        <BarChart data={categoryCounts}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend content={() => (
                                <ul style={{ listStyleType: 'none', padding: 0 }}>
                                    {categoryCounts.map((entry, index) => (
                                        <li key={`item-${index}`} style={{ color: COLORSBAR1[index % COLORSBAR1.length] }}>
                                            <span style={{ color: COLORSBAR1[index % COLORSBAR1.length], marginRight: 5 }}>■</span>
                                            {entry.name}
                                        </li>
                                    ))}
                                </ul>
                            )}/>
                            <Bar dataKey="value">
                                {categoryCounts.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORSBAR1[index % COLORSBAR1.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
                {activeSection === 'bar2' && (
                    <ResponsiveContainer width="80%" height="100%">
                        <BarChart data={genderCounts} layout="vertical" margin={{ top: 20, right: 20, left: 100, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="name" />
                            <Tooltip />
                            <Legend content={() => (
                                <ul style={{ listStyleType: 'none', padding: 0 }}>
                                    {genderCounts.map((entry, index) => (
                                        <li key={`item-${index}`} style={{ color: COLORSBAR2[index % COLORSBAR2.length] }}>
                                            <span style={{ color: COLORSBAR2[index % COLORSBAR2.length], marginRight: 5 }}>■</span>
                                            {entry.name}
                                        </li>
                                    ))}
                                </ul>
                            )}/>
                            <Bar dataKey="value">
                                {genderCounts.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORSBAR2[index % COLORSBAR2.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}

                {activeSection === 'line' && (
                    <ResponsiveContainer width="80%" height="100%">
                        <LineChart data={programCounts}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="value" stroke="#8884d8" activeDot={{ r: 8 }} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}

export default Statistics;
